import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  Typography,
  TextField,
  Button,
  Paper,
  Grid,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Skeleton,
  Backdrop,
  CircularProgress,
  Chip,
  Container,
  Fade,
  Zoom,
} from '@mui/material';
import { AccountBalanceWallet as AccountBalanceWalletIcon, Security as SecurityIcon } from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';
import { enqueueSnackbar } from 'notistack';
import { useSelector } from 'react-redux';
import { ConfirmationModal } from '../../components/ConfirmationModal';
import { useGetLastFiveTransactionsQuery, useSendMoneyMutation } from './services/services';
import { useCommission } from '../../hooks/useCommission';
import { usePermission } from '../../hooks/usePermission';
import TransactionSummaryCard from '../../components/TransactionSummaryCard';
import Balance from '../../components/balance';
import FintechLoader from '../../components/FintechLoader';
import { useCompanyBalance } from '../../hooks/useCompanyBalance';

interface SendMoneyFormData {
  walletNo: string;
  value: string;
  currency: string;
  description: string;
}

interface Wallet {
  currencyId: number;
  name: string;
  balance: number;
}

const SendMoneyPage: React.FC = () => {
  const { commission, calculateCommission } = useCommission();
  const { hasPermission: showCompanyBalance, isLoading: permissionLoading } = usePermission({
    code: 'Corporate.POST.Reading.CüzdandanCüzdanaParaTransferi'
  });

  const [spinner, setSpinner] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [pendingTransfer, setPendingTransfer] = useState<SendMoneyFormData | null>(null);
  const [pendingValue, setPendingValue] = useState<number>(0);
  const [sendMoney] = useSendMoneyMutation();
  const { currentUserState } = useSelector((state: any) => state.currentUserState);
  const walletState = useSelector((state: any) => state.walletsSlice.wallets);
  const { data: lastFiveTransactions, isLoading: isTransactionsLoading, error: transactionsError } = useGetLastFiveTransactionsQuery(
    { customerId: currentUserState.companyId, processType: 1 },
    { skip: !currentUserState?.companyId || permissionLoading || !showCompanyBalance }
  );
  const { control, handleSubmit, formState: { errors }, reset, watch, setValue } = useForm<SendMoneyFormData>({
    defaultValues: {
      currency: '1',
      value: '',
      walletNo: '',
      description: ''
    }
  });
  const watchedCurrency = watch('currency');
  const watchedValue = watch('value');
  useEffect(() => {
    const optionValues = walletState.map((w: Wallet) => String(w.currencyId));
    if (optionValues.length === 0) return;

    const currentCurrency = String(watchedCurrency ?? '');
    if (!optionValues.includes(currentCurrency)) {
      setValue('currency', optionValues[0]);
    }
  }, [walletState, watchedCurrency, setValue]);

  useEffect(() => {
    if (!watchedValue) return;

    const digits = watchedValue.replace(/\D/g, '');
    if (!digits) return;

    const reformattedValue = formatMoneyInput(digits, watchedCurrency || '1');
    if (reformattedValue !== watchedValue) {
      setValue('value', reformattedValue);
    }
  }, [watchedCurrency, watchedValue, setValue]);

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    const day = date.getDate();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    return `${day}/${month}/${year % 100}: ${hours}:${minutes}`;
  };
  const { data: getBalance, isLoading: isBalanceLoading, refetch: refetchBalance } = useCompanyBalance(currentUserState?.companyId)

  const formatMoney = (value: number, currency: string | number = '1'): string => {
    const currencyStr = typeof currency === 'number' ? currency.toString() : currency;
    const currencyLabel = currencyToForm(parseInt(currencyStr));
    const formatter = new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: currencyLabel === 'TL' ? 'TRY' : currencyLabel,
    });
    return formatter.format(value);
  };

  const currencyToForm = (currencyId: number): string => {
    switch (currencyId) {
      case 2: return 'USD';
      case 3: return 'EUR';
      case 4: return 'AU';
      default: return 'TL';
    }
  };

  const getStatus = (status: number): string => {
    switch (status) {
      case 0: return 'Beklemede';
      case 1: return 'Başarılı';
      case 2: return 'İptal';
      case 3: return 'Red';
      default: return 'Bilinmiyor';
    }
  };

  const getStatusColor = (status: number): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
    switch (status) {
      case 0: return 'warning';
      case 1: return 'success';
      case 2: return 'error';
      case 3: return 'error';
      default: return 'default';
    }
  };

  const formatMoneyInput = (value: string, currencyId: string) => {
    if (!value) return '';

    const currency = currencyToForm(parseInt(currencyId));
    let numericValue = value.replace(/\D/g, '');

    if (!numericValue) return '';

    numericValue = numericValue.padStart(3, '0');
    const integerPart = numericValue.slice(0, -2);
    const decimalPart = numericValue.slice(-2);

    const integerFormatted = new Intl.NumberFormat('tr-TR').format(BigInt(integerPart));
    const symbols: Record<string, string> = { TL: '₺', USD: '$', EUR: '€', AU: 'AU ' };
    const symbol = symbols[currency] || '₺';

    return `${symbol}${integerFormatted},${decimalPart}`;
  };

  const parseFormattedAmount = (formattedValue: string): number => {
    const normalized = formattedValue
      .replace(/[^0-9.,-]/g, '')
      .replaceAll('.', '')
      .replace(',', '.');

    return parseFloat(normalized);
  };

  const getCurrencyFromWalletNo = (walletNo: string) => {
    const cleanWalletNo = walletNo.replace(/[^0-9]/g, '').substring(0, 12);
    const regex = /^[1-2]\d{10}[1-3]$/;

    if (regex.test(cleanWalletNo)) {
      const currencyId = cleanWalletNo.charAt(cleanWalletNo.length - 1);
      setValue('currency', currencyId);
    }
    setValue('walletNo', cleanWalletNo);
  };

  const onSubmit = async (data: SendMoneyFormData) => {
    if (isSubmitting) return;
    setIsSubmitting(true);

    try {
      const formattedValue = parseFormattedAmount(data.value);

      if (formattedValue < 1) {
        enqueueSnackbar('Tutar 1,00 ve üzeri olmalıdır', { variant: 'error' });
        setIsSubmitting(false);
        return;
      }
      try {
        await calculateCommission({
          value: formattedValue,
          currencyId: data.currency,
          type: "1",
          companyId: currentUserState.companyId,
          customerId: currentUserState.customerId
        });
        // Pending transfer bilgilerini sakla
        setPendingTransfer(data);
        setPendingValue(formattedValue);
        setConfirmDialogOpen(true);
      } catch (error) {

      }
    } catch (error) {
      enqueueSnackbar('Bir hata oluştu', { variant: 'error' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const confirmSendMoney = async () => {
    setConfirmDialogOpen(false);
    setSpinner(true);

    if (!pendingTransfer) {
      enqueueSnackbar('Transfer bilgileri bulunamadı', { variant: 'error' });
      setSpinner(false);
      return;
    }

    try {
      const sendMoneyRequest = {
        data: [
          {
            currencyId: pendingTransfer.currency,
            value: pendingValue,
            receiverWalletNo: pendingTransfer.walletNo,
            description: pendingTransfer.description,
            status: null,
            message: null,
            success: null,
            statusClass: null,
            statusDisplay: null
          }
        ],
        companyId: currentUserState.companyId,
        customerId: currentUserState.customerId
      };

      const result: any = await sendMoney(sendMoneyRequest);
      const responseData = result?.data;
      const transactionData = responseData?.data?.[0];
      const isSuccess = responseData?.success === true;
      const message =
        transactionData?.message ||
        responseData?.message;

      if (isSuccess) {
        const selectedCurrency = pendingTransfer.currency;
        enqueueSnackbar(message || 'İşlem isteğiniz onaya gönderildi.', { variant: 'success' });
        await refetchBalance();
        reset({
          walletNo: '',
          value: '',
          description: '',
          currency: selectedCurrency
        });
        setPendingTransfer(null);
        setPendingValue(0);
      } else if (result.error) {
        // Sadece backend'ten gelen message'ı göster
        const errorMessage = result?.error?.data?.message;
        if (errorMessage) {
          enqueueSnackbar(errorMessage, { variant: 'error' });
        }
        setSpinner(false);
        return;
      } else {
        enqueueSnackbar(message || 'İşlem başarısız', { variant: 'error' });
        setSpinner(false);
        return;
      }

    } catch (error) {
      // Beklenmeyen hatalar için sadece console'a log
      console.error('Para gönderme işlemi hatası:', error);
    } finally {
      setSpinner(false);
    }
  };

  // Yeni: Tüm sayfa için loading state
  const isLoading = isBalanceLoading || isTransactionsLoading || permissionLoading;

  if (!permissionLoading && !showCompanyBalance) {
    return (
      <Box sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center'
      }}>
        <Card sx={{
          maxWidth: 500,
          p: 4,
          borderRadius: 3,
          boxShadow: '0 8px 32px rgba(239, 68, 68, 0.1)',
          background: 'rgba(255,255,255,0.9)',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(252, 165, 165, 0.3)',
          textAlign: 'center'
        }}>
          <SecurityIcon sx={{ fontSize: 64, color: '#ef4444', mb: 3 }} />
          <Typography variant="h5" sx={{ fontWeight: 600, color: '#dc2626', mb: 2 }}>
            Erişim Engellendi
          </Typography>
          <Typography variant="body1" sx={{ color: '#7f1d1d', lineHeight: 1.6 }}>
            Bu sayfaya erişim yetkiniz bulunmamaktadır. Sistem yöneticinizle iletişime geçiniz.
          </Typography>
        </Card>
      </Box>
    );
  }

  return (
    <>
      {isLoading ? (
        // Kendi FintechLoader animasyonumuz
        <Container maxWidth={false} sx={{
          flexGrow: 1,
          padding: { xs: '110px 8px 16px 8px', sm: '110px 12px 24px 12px', md: '110px 32px 40px 32px', lg: '110px 40px 40px 40px' },
          background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          <Box sx={{ textAlign: 'center' }}>
            <FintechLoader />
          </Box>
        </Container>
      ) : (
        <Fade in={!isLoading} timeout={600}>
          <Container maxWidth={false} sx={{
            flexGrow: 1,
            padding: { xs: '110px 8px 16px 8px', sm: '110px 12px 24px 12px', md: '110px 32px 40px 32px', lg: '110px 40px 40px 40px' },
            background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
            minHeight: '100vh'
          }}>

            {/* Main Dashboard Container */}
            <Paper
              elevation={0}
              sx={{
                borderRadius: 4,
                background: 'linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.8) 100%)',
                backdropFilter: 'blur(20px)',
                border: '1px solid rgba(255,255,255,0.3)',
                boxShadow: '0 20px 40px rgba(0,0,0,0.08)',
                overflow: 'hidden',
                position: 'relative',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: '6px',
                  background: 'linear-gradient(90deg, #1e3c72 0%, #2a5298 25%, #3b82f6 50%, #2a5298 75%, #1e3c72 100%)',
                }
              }}
            >
              <Box sx={{
                p: 0,
                background: 'linear-gradient(135deg, rgba(30, 60, 114, 0.03) 0%, rgba(42, 82, 152, 0.06) 100%)',
                borderBottom: '1px solid rgba(226, 232, 240, 0.8)'
              }}>
                {/* Header */}
                <Box sx={{ px: { xs: 1.5, sm: 2.5, md: 4, lg: 5 }, pt: 4, pb: 1 }}>
                  <Balance />
                </Box>
                {/* Content */}
                <Box sx={{ px: { xs: 1.5, sm: 2.5, md: 4, lg: 5 }, pb: 5 }}>
                  <Backdrop open={spinner} sx={{ color: '#fff', zIndex: 9999 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                      <CircularProgress />
                      <Typography variant="body1" fontWeight="600">
                        Lütfen Bekleyiniz
                      </Typography>
                    </Box>
                  </Backdrop>
                  {/* Send Money Section */}
                  {showCompanyBalance && (
                    <Grid container spacing={3} sx={{ mb: 3, mt: 2 }}>
                      {/* Send Money Form */}
                      <Fade in={true} timeout={800}>
                        <Grid item xs={12} lg={6}>
                          <Box sx={{ flex: 1, bgcolor: "background.paper", borderRadius: 2, maxHeight: 502, overflowY: "auto" }}>
                            <Box sx={{ borderBottom: 1, padding: "15px", borderColor: 'divider', display: "flex", justifyContent: "space-between", alignItems: "center", }}>
                              <Typography variant="h6" fontWeight="600">
                                Hesabına Para Gönder
                              </Typography>
                            </Box>
                            <Box sx={{ p: 2, flex: 1, overflow: 'auto' }}>
                              <form onSubmit={handleSubmit(onSubmit)}>
                                <Grid container spacing={3}>
                                  {/* Wallet Number */}
                                  <Grid item xs={12}>
                                    <Controller
                                      name="walletNo"
                                      control={control}
                                      rules={{
                                        required: 'Lütfen 12 haneli alıcı hesap no giriniz.',
                                        minLength: { value: 12, message: 'Lütfen 12 haneli alıcı hesap no giriniz.' }
                                      }}
                                      render={({ field }) => (
                                        <TextField
                                          {...field}
                                          fullWidth
                                          label="Alıcı Hesap No"
                                          placeholder="Hesap Numarasını Giriniz"
                                          required
                                          error={!!errors.walletNo}
                                          helperText={errors.walletNo?.message}
                                          onChange={(e) => {
                                            const value = e.target.value.replace(/[^0-9]/g, '').substring(0, 12);
                                            field.onChange(value);
                                            getCurrencyFromWalletNo(value);
                                          }}
                                          inputProps={{ maxLength: 12 }}
                                        />
                                      )}
                                    />
                                  </Grid>

                                  {/* Amount */}
                                  <Grid item xs={12}>
                                    <Controller
                                      name="value"
                                      control={control}
                                      rules={{
                                        required: 'Lütfen göndermek istediğiniz tutarı giriniz.',
                                        validate: (value) => {
                                          const numValue = parseFormattedAmount(value);
                                          return numValue >= 1 || 'Tutar 1,00 ve üzeri olmalıdır';
                                        }
                                      }}
                                      render={({ field }) => (
                                        <TextField
                                          {...field}
                                          fullWidth
                                          label="Tutar"
                                          placeholder="Tutar Giriniz"
                                          required
                                          error={!!errors.value}
                                          helperText={errors.value?.message}
                                          onChange={(e) => {
                                            const input = e.target as HTMLInputElement;
                                            const digits = input.value.replace(/\D/g, '');
                                            if (digits.length > 16) return;
                                            const formatted = formatMoneyInput(input.value, watchedCurrency);
                                            field.onChange(formatted);
                                            requestAnimationFrame(() => {
                                              input.setSelectionRange(formatted.length, formatted.length);
                                            });
                                          }}
                                          onFocus={(e) => {
                                            const input = e.target as HTMLInputElement;
                                            requestAnimationFrame(() => {
                                              input.setSelectionRange(input.value.length, input.value.length);
                                            });
                                          }}
                                          onPaste={(e) => {
                                            e.preventDefault();
                                            const digits = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 16);
                                            if (!digits) return;
                                            const formatted = formatMoneyInput(digits, watchedCurrency);
                                            field.onChange(formatted);
                                            const input = e.target as HTMLInputElement;
                                            requestAnimationFrame(() => {
                                              input.setSelectionRange(formatted.length, formatted.length);
                                            });
                                          }}
                                          onKeyDown={(e) => {
                                            if (e.key === 'Backspace' || e.key === 'Delete') {
                                              e.preventDefault();
                                              const input = e.target as HTMLInputElement;
                                              const value = input.value;
                                              const cursorPos = input.selectionStart ?? value.length;
                                              const selEnd = input.selectionEnd ?? cursorPos;
                                              const digits = value.replace(/\D/g, '');
                                              let newDigits: string;
                                              if (selEnd > cursorPos) {
                                                const digitsBeforeStart = value.slice(0, cursorPos).replace(/\D/g, '').length;
                                                const digitsBeforeEnd = value.slice(0, selEnd).replace(/\D/g, '').length;
                                                newDigits = digits.slice(0, digitsBeforeStart) + digits.slice(digitsBeforeEnd);
                                              } else if (e.key === 'Backspace') {
                                                let digitIndex = -1;
                                                for (let i = cursorPos - 1; i >= 0; i--) {
                                                  if (/\d/.test(value[i])) {
                                                    digitIndex = value.slice(0, i).replace(/\D/g, '').length;
                                                    break;
                                                  }
                                                }
                                                if (digitIndex === -1) return;
                                                newDigits = digits.slice(0, digitIndex) + digits.slice(digitIndex + 1);
                                              } else {
                                                let digitIndex = -1;
                                                for (let i = cursorPos; i < value.length; i++) {
                                                  if (/\d/.test(value[i])) {
                                                    digitIndex = value.slice(0, i).replace(/\D/g, '').length;
                                                    break;
                                                  }
                                                }
                                                if (digitIndex === -1) return;
                                                newDigits = digits.slice(0, digitIndex) + digits.slice(digitIndex + 1);
                                              }
                                              const formatted = formatMoneyInput(newDigits, watchedCurrency);
                                              field.onChange(formatted);
                                              requestAnimationFrame(() => {
                                                input.setSelectionRange(formatted.length, formatted.length);
                                              });
                                              return;
                                            }
                                            const allowedKeys = ['Tab', 'Escape', 'Enter', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'];
                                            if (!allowedKeys.includes(e.key) && (e.key < '0' || e.key > '9') && !e.ctrlKey && !e.metaKey) {
                                              e.preventDefault();
                                            }
                                          }}
                                        />
                                      )}
                                    />
                                  </Grid>

                                  {/* Currency */}
                                  <Grid item xs={12}>
                                    <Controller
                                      name="currency"
                                      control={control}
                                      rules={{ required: 'Para Birimi Alanı boş olamaz!' }}
                                      render={({ field }) => {
                                        const optionValues = walletState.map((w: Wallet) => String(w.currencyId));
                                        const safeValue = optionValues.length === 0
                                          ? ''
                                          : (optionValues.includes(String(field.value)) ? String(field.value) : optionValues[0]);
                                        return (
                                          <FormControl fullWidth required error={!!errors.currency}>
                                            <InputLabel>Para Birimi</InputLabel>
                                            <Select
                                              {...field}
                                              value={safeValue}
                                              onChange={(e) => field.onChange(e.target.value)}
                                              label="Para Birimi"
                                              displayEmpty
                                            >
                                              {walletState.length === 0 && (
                                                <MenuItem value="" disabled>
                                                  <em>Yükleniyor...</em>
                                                </MenuItem>
                                              )}
                                              {walletState.map((wallet: Wallet) => (
                                                <MenuItem key={wallet.currencyId} value={wallet.currencyId.toString()}>
                                                  {currencyToForm(wallet.currencyId)}
                                                </MenuItem>
                                              ))}
                                            </Select>
                                          </FormControl>
                                        );
                                      }}
                                    />
                                  </Grid>

                                  {/* Description */}
                                  <Grid item xs={12}>
                                    <Controller
                                      name="description"
                                      control={control}
                                      render={({ field }) => (
                                        <TextField
                                          {...field}
                                          fullWidth
                                          label="Açıklama"
                                          placeholder="Açıklama"
                                          multiline
                                          rows={3}
                                        />
                                      )}
                                    />
                                  </Grid>

                                  {/* Submit Button */}
                                  <Grid item xs={12}>
                                    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                                      <Button
                                        variant="contained"
                                        color="primary"
                                        type="submit"
                                        disabled={isSubmitting}
                                        startIcon={isSubmitting ? <CircularProgress size={16} color="inherit" /> : null}
                                        sx={{
                                          width: '120px',
                                          height: '36px',
                                          fontSize: '0.875rem',
                                          fontWeight: 600,
                                          textTransform: 'none',
                                          whiteSpace: 'nowrap',
                                          boxShadow: '0 2px 8px rgba(25, 118, 210, 0.25)',
                                          transition: 'all 0.2s ease',
                                          '&:hover': {
                                            transform: 'translateY(-1px)',
                                            boxShadow: '0 4px 12px rgba(25, 118, 210, 0.35)',
                                          }
                                        }}
                                      >
                                        {isSubmitting ? 'Gönderiliyor...' : 'Gönder'}
                                      </Button>
                                    </Box>
                                  </Grid>
                                </Grid>
                              </form>
                            </Box>
                          </Box>
                        </Grid>
                      </Fade>


                      {/* Recent Transactions */}
                      <Fade in={true} timeout={800}>
                        <Grid item xs={12} lg={6}>
                          <Box sx={{ flex: 1, bgcolor: 'background.paper', borderRadius: 2, maxHeight: 502, overflowY: 'auto', boxShadow: 1 }}>
                            <Box sx={{ borderBottom: 1, padding: "15px", borderColor: 'divider', display: "flex", justifyContent: "space-between", alignItems: "center", }}>
                              <Typography variant="h6" fontWeight={600}>Yapılan Son İşlemler</Typography>
                            </Box>
                            <Box sx={{ p: 2.5, flex: 1, overflow: 'auto' }}>
                              <TableContainer sx={{ maxHeight: 380 }}>
                                <Table stickyHeader size="small">
                                  <TableHead>
                                    <TableRow>
                                      <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', background: 'background.paper' }}>İşlem Tarihi</TableCell>
                                      <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', background: 'background.paper' }}>Gönderici Hesap No</TableCell>
                                      <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', background: 'background.paper' }}>Tutar</TableCell>
                                      <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', background: 'background.paper' }}>Alıcı Adı</TableCell>
                                      <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', background: 'background.paper' }}>Alıcı Hesap No</TableCell>
                                      <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', background: 'background.paper' }}>Durum</TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {isTransactionsLoading ? (
                                      // Loading skeleton
                                      Array.from(new Array(3)).map((_, index) => (
                                        <TableRow key={index}>
                                          <TableCell><Skeleton variant="text" width="80%" /></TableCell>
                                          <TableCell><Skeleton variant="text" width="60%" /></TableCell>
                                          <TableCell><Skeleton variant="text" width="60%" /></TableCell>
                                          <TableCell><Skeleton variant="text" width="70%" /></TableCell>
                                          <TableCell><Skeleton variant="text" width="70%" /></TableCell>
                                          <TableCell><Skeleton variant="rectangular" width={60} height={24} /></TableCell>
                                        </TableRow>
                                      ))
                                    ) : lastFiveTransactions?.data && lastFiveTransactions.data.length > 0 ? (
                                      lastFiveTransactions.data.map((row: any, index: any) => (
                                        <TableRow key={index} hover>
                                          <TableCell >
                                            <Typography variant="body2" sx={{ fontSize: '0.75rem', fontWeight: 500 }}>
                                              {formatDate(row.cDate)}
                                            </Typography>
                                          </TableCell>
                                          <TableCell >
                                            <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                                              {row.senderWalletNo}
                                            </Typography>
                                          </TableCell>
                                          <TableCell sx={{ fontSize: '0.75rem', fontWeight: 600 }}>
                                            <Typography variant="body2" sx={{ fontSize: '0.75rem', fontWeight: 600 }}>
                                              {formatMoney(row.value, row.currency)}
                                            </Typography>
                                          </TableCell>
                                          <TableCell >
                                            <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                                              {row.receiverName}
                                            </Typography>
                                          </TableCell>
                                          <TableCell >
                                            <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                                              {row.receiverWalletNo}
                                            </Typography>
                                          </TableCell>
                                          <TableCell>
                                            <Chip
                                              label={getStatus(row.status)}
                                              color={getStatusColor(row.status)}
                                              size="small"
                                              sx={{ fontSize: '0.8rem' }}
                                            />
                                          </TableCell>
                                        </TableRow>
                                      ))
                                    ) : (
                                      <TableRow>
                                        <TableCell colSpan={6} align="center" sx={{ border: 'none' }}>
                                          <Box sx={{ py: 4, textAlign: 'center' }}>
                                            <AccountBalanceWalletIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
                                            <Typography variant="body2" color="textSecondary">
                                              Henüz işlem bulunmamaktadır.
                                            </Typography>
                                          </Box>
                                        </TableCell>
                                      </TableRow>
                                    )}
                                    {transactionsError && (
                                      <TableRow>
                                        <TableCell colSpan={6} align="center">
                                          <Alert severity="error" sx={{ m: 2 }}>
                                            İşlemler yüklenirken bir hata oluştu.
                                          </Alert>
                                        </TableCell>
                                      </TableRow>
                                    )}
                                  </TableBody>
                                </Table>
                              </TableContainer>
                            </Box>
                          </Box>
                        </Grid>
                      </Fade>
                    </Grid>
                  )}

                  {/* Confirmation Dialog */}
                  <ConfirmationModal
                    isOpen={confirmDialogOpen}
                    onClose={() => setConfirmDialogOpen(false)}
                    onConfirm={confirmSendMoney}
                    title="Hesabına Para Gönder"
                    confirmButtonText="Gönder"
                    closeButtonText="İptal"
                    isLoading={false}
                    optionalWidth="800px !important"
                  >
                    {pendingTransfer && (
                      <TransactionSummaryCard
                        firstFieldName="Hesap Numarası"
                        walletNo={pendingTransfer?.walletNo}
                        value={pendingTransfer?.value}
                        commissionValue={commission?.valueFee || 0}
                        totalValue={commission?.total || 0}
                        currencyLabel={currencyToForm(Number(pendingTransfer?.currency))}
                        formatMoney={formatMoney}
                      />
                    )}
                  </ConfirmationModal>
                </Box>
              </Box>
            </Paper>
          </Container>
        </Fade>
      )}
    </>
  );
};

export default SendMoneyPage;
